/**
 * History Panel - Undo/Redo controls and change history
 */

import { useState } from 'react';
import {
  Undo2,
  Redo2,
  History,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  Edit3,
} from 'lucide-react';
import { Button } from '../ui';
import { useConfigurator, selectCanUndo, selectCanRedo } from '../../hooks/useConfigurator';
import { ConfigChange, summarizeChanges, groupChangesByCategory } from '../../utils/configDiff';
import { clsx } from 'clsx';

interface HistoryPanelProps {
  className?: string;
  compact?: boolean;
}

export function HistoryPanel({ className, compact = false }: HistoryPanelProps) {
  const [showDetails, setShowDetails] = useState(false);
  const { history, historyIndex, undo, redo, getLastChanges } = useConfigurator();
  const canUndo = useConfigurator(selectCanUndo);
  const canRedo = useConfigurator(selectCanRedo);

  const lastChanges = getLastChanges();
  const changesSummary = summarizeChanges(lastChanges);

  if (compact) {
    return (
      <div className={clsx('flex items-center gap-1', className)}>
        <Button
          variant="ghost"
          size="sm"
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          className="h-8 w-8 p-0"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
          className="h-8 w-8 p-0"
        >
          <Redo2 className="h-4 w-4" />
        </Button>
        {history.length > 0 && (
          <span className="text-xs text-gray-400 ml-1">
            {historyIndex + 1}/{history.length}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={clsx('rounded-lg border border-gray-200 bg-white', className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">History</span>
          {history.length > 0 && (
            <span className="text-xs text-gray-400">
              ({historyIndex + 1}/{history.length})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={undo}
            disabled={!canUndo}
            title="Undo"
            className="h-7 px-2"
          >
            <Undo2 className="h-3.5 w-3.5 mr-1" />
            Undo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={redo}
            disabled={!canRedo}
            title="Redo"
            className="h-7 px-2"
          >
            Redo
            <Redo2 className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>

      {/* Last changes summary */}
      {lastChanges.length > 0 && (
        <div className="px-3 py-2">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex w-full items-center justify-between text-left"
          >
            <span className="text-sm text-gray-600">
              Last change: <span className="font-medium">{changesSummary}</span>
            </span>
            {showDetails ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {showDetails && (
            <div className="mt-2 space-y-1">
              <ChangesList changes={lastChanges} />
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {history.length === 0 && (
        <div className="px-3 py-4 text-center text-sm text-gray-400">
          No history yet. Changes will appear here.
        </div>
      )}
    </div>
  );
}

/**
 * Render a list of changes
 */
function ChangesList({ changes }: { changes: ConfigChange[] }) {
  const grouped = groupChangesByCategory(changes);

  return (
    <div className="space-y-2">
      {Object.entries(grouped).map(([category, categoryChanges]) => (
        <div key={category}>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            {category}
          </div>
          <div className="space-y-0.5">
            {categoryChanges.map((change, idx) => (
              <ChangeItem key={idx} change={change} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Single change item
 */
function ChangeItem({ change }: { change: ConfigChange }) {
  const getIcon = () => {
    switch (change.type) {
      case 'added':
        return <Plus className="h-3 w-3 text-green-500" />;
      case 'removed':
        return <Minus className="h-3 w-3 text-red-500" />;
      case 'modified':
        return <Edit3 className="h-3 w-3 text-blue-500" />;
    }
  };

  const getBgColor = () => {
    switch (change.type) {
      case 'added':
        return 'bg-green-50';
      case 'removed':
        return 'bg-red-50';
      case 'modified':
        return 'bg-blue-50';
    }
  };

  return (
    <div className={clsx('flex items-start gap-2 rounded px-2 py-1 text-xs', getBgColor())}>
      <span className="mt-0.5">{getIcon()}</span>
      <span className="text-gray-700">{change.description}</span>
    </div>
  );
}

/**
 * Keyboard shortcuts hook for undo/redo with optional toast feedback
 */
export function useHistoryKeyboardShortcuts(options?: { showToast?: boolean }) {
  const { undo, redo, history, historyIndex } = useConfigurator();
  const canUndo = useConfigurator(selectCanUndo);
  const canRedo = useConfigurator(selectCanRedo);

  const handleKeyDown = (e: KeyboardEvent) => {
    // Ctrl/Cmd + Z for undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      if (canUndo) {
        undo();
        if (options?.showToast) {
          // Dispatch custom event for toast notification
          const snapshot = history[historyIndex - 1];
          window.dispatchEvent(
            new CustomEvent('hermit:undo', { detail: { label: snapshot?.label } })
          );
        }
      }
    }
    // Ctrl/Cmd + Shift + Z for redo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
      e.preventDefault();
      if (canRedo) {
        redo();
        if (options?.showToast) {
          const snapshot = history[historyIndex + 1];
          window.dispatchEvent(
            new CustomEvent('hermit:redo', { detail: { label: snapshot?.label } })
          );
        }
      }
    }
    // Ctrl/Cmd + Y for redo (alternative)
    if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
      e.preventDefault();
      if (canRedo) {
        redo();
        if (options?.showToast) {
          const snapshot = history[historyIndex + 1];
          window.dispatchEvent(
            new CustomEvent('hermit:redo', { detail: { label: snapshot?.label } })
          );
        }
      }
    }
  };

  return handleKeyDown;
}
